import { TaskData } from 'bookitup-form-builder';
import SemanticForm, { FormCustomItem, FormStyleAttributes } from '../../Types/SemanticForm';
import { ajaxActions } from '../AjaxActions';
import { ToastS } from '../ToastS';

const BASE_URL = process.env.REACT_APP_REQUEST_SERVICE_URL;
const S3_CONNECT_URL = process.env.REACT_APP_CONNECT_SERVICE_URL;

const HCAPTCHA_FIELD = {
  id: 'C90507E8-BB53-41E2-9787-20C82CDE31C9',
  key: 'HCaptcha',
  element: 'HCaptcha',
  text: 'HCaptcha',
  group_name: 'Formular',
  required: true,
  deleteForbidden: false,
  canHavePageBreakBefore: true,
  canHaveAlternateForm: true,
  canHaveDisplayHorizontal: true,
  canHaveOptionCorrect: true,
  canHaveOptionValue: true,
  canPopulateFromApi: true,
  sitekey: '4548bbb8-df9c-4210-b540-f3f082b84464',
  field_name: 'HCaptcha_AA15E26C-6625-4DE0-B0BF-3C3B7C773110',
  label: 'HCaptcha',
};

export const fetchFormTemplates = () => ajaxActions.get(`${BASE_URL}/semanticform/templates`).then((resp) => resp);

export const generateFormIframe = (profileId: number, formId: string) =>
  ajaxActions.get(`${BASE_URL}/js/${profileId}/form/${formId}`).then((resp) => resp);

export const generateFormScript = (profileId: number, formId: string, includeStyles?: boolean) =>
  ajaxActions
    .get(`${BASE_URL}/js/${profileId}/form/${formId}/js${includeStyles ? '?includeStyles=true' : ''}`)
    .then((resp) => resp);

export const createSemanticFormIframeUrl = (profileId: number, formId: string) =>
  `${S3_CONNECT_URL}/bookitup-forms/${profileId}/html/bookitup-form-${formId}.html`;

const createSemanticFormIframeUrlV2 = (profileId: number, formId: string) =>
  `${S3_CONNECT_URL}/bookitup-forms/${profileId}/html/bookitup-form-${formId}.html`;

const createStandaloneSemanticFormIframeUrl = (profileId: number, formId: string) => {
  let url = `https://connect.bookitup.de/iframe-page.html?profileId=${profileId}&formId=${formId}`;
  if (S3_CONNECT_URL !== 'https://connect.bookitup.de') {
    url += `&baseUrl=${S3_CONNECT_URL}`;
  }
  return url;
};

export const createSemanticFormStylesUrl = (profileId: number, formId: string) =>
  `${S3_CONNECT_URL}/bookitup-forms/${profileId}/css/bookitup-form-${formId}.css`;

export const createSemanticFormScriptUrl = (profileId: number, formId: string) =>
  `${S3_CONNECT_URL}/bookitup-forms/${profileId}/js/bookitup-script-${formId}-min.js`;

export const jsEmbeddingCodeSnippet = `
  <link rel="stylesheet" href="https://connect.bookitup.de/external/css/fontawesome@6.2.1/all.css">
  <link rel="stylesheet" href="https://connect.bookitup.de/external/css/bootstrap@4.4.1/bootstrap.min.css">
  <link rel="stylesheet" type="text/css" href="https://connect.bookitup.de/external/css/bookitup-form-builder@1.2.9/app.css">
  ~!~CUSTOM_STYLES~!~
    <div class="form-container">
      <div class="form-background"></div>
      <div id="form-generator" class="form-horizontal"></div>
    </div>

  <script src="https://connect.bookitup.de/external/js/react@16.8.6/react.production.min.js" crossorigin></script>
  <script src="https://connect.bookitup.de/external/js/react-dom@16.8.6/react-dom.production.min.js" crossorigin></script>
  <script src="https://connect.bookitup.de/external/js/bookitup-form-builder@1.2.9/app.js"></script>
  <script src="~!~CUSTOM_SCRIPT~!~"></script>
  `;

export const iFrameEmbeddingCodeSnippet = `
  <script src="https://connect.bookitup.de/external/js/iframe-resizer@4.3.6/iframeResizer.min.js"></script>
  <iframe id="formIframe" style="width: 1px;  min-width: 100%; border: none;" src="~!~CUSTOM_IFRAME_URL~!~"></iframe>
  <script>var f=document.getElementById("formIframe");f.addEventListener("load",function(){iFrameResize({heightCalculationMethod:"bodyScroll",checkOrigin:false},"#formIframe")});</script>
  `;

const create = async (form: Partial<SemanticForm>, templateId?: string): Promise<SemanticForm | null> => {
  const res = await ajaxActions.post(`${BASE_URL}/semanticform${templateId ? `?templateId=${templateId}` : ''}`, form);
  if (res.ok) {
    ToastS.success('create-form', 'Formular erfolgreich erstellt.');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const getForms = async (): Promise<SemanticForm[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/semanticform`);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const copy = async (originFormId: string): Promise<SemanticForm | null> => {
  const res = await ajaxActions.post(`${BASE_URL}/semanticform/copy/${originFormId}`);
  if (res.status === 201) {
    ToastS.success('copy-created', 'Kopie erfolgreich erstellt');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const getForm = async (formId: string): Promise<SemanticForm | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/semanticform/${formId}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const patch = async (formId: string, form: Partial<SemanticForm>): Promise<SemanticForm | null> => {
  precheckForm(form);
  const res = await ajaxActions.patch(`${BASE_URL}/semanticform/${formId}`, form);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const precheckForm = (form: Partial<SemanticForm>) => {
  if (form.task_data) {
    const elementsIds = form.task_data.map((e) => e.id);
    // Remove children from container elements, which are no longer exists
    const removeNonExistingChildren = (data: any[]) => {
      data.forEach((element) => {
        // Check for container elements
        const { childItems } = element;
        if (childItems) {
          for (let childItem of childItems) {
            if (childItem !== null && !elementsIds.includes(childItem)) {
              // Nullify child element if doesn't exists in form
              childItem = null;
            }
          }
        }
      });
      return data;
    };
    form.task_data = removeNonExistingChildren(form.task_data);
  }
};

const patchStyles = async (formId: string, styles: Partial<FormStyleAttributes>): Promise<boolean> => {
  const res = await ajaxActions.patch(`${BASE_URL}/semanticform/${formId}/styles`, styles);
  if (res.ok) {
    return true;
  }
  ToastS.generalError();
  return false;
};

const deleteForm = async (formId: string): Promise<boolean> => {
  const res = await ajaxActions.del(`${BASE_URL}/semanticform/${formId}`).then((resp) => resp);
  if (res.ok) {
    ToastS.info('delete-form', 'Formular wurde gelöscht');
    return true;
  }
  ToastS.generalError();
  return false;
};

const syncFormFiles = async (formId: string): Promise<boolean> => {
  const res = await ajaxActions.get(`${BASE_URL}/js/form/${formId}/sync`);
  if (res.ok) {
    return true;
  }
  ToastS.error('form-preview-err', 'Die Vorschau kann derzeit nicht aktualisiert werden.');
  return false;
};

const generateIframeCode = (formId: string, profileId: number) => {
  const iframeUrl = createSemanticFormIframeUrl(profileId, formId);
  return iFrameEmbeddingCodeSnippet.replace('~!~CUSTOM_IFRAME_URL~!~', iframeUrl);
};

const generateIframeCodeV2 = (formId: string, profileId: number) => `<div id="bookitup-form"></div>
<script
  src="https://connect.bookitup.de/iframe-form.js"
  data-iframe-src="${createSemanticFormIframeUrl(profileId, formId)}">
</script>`;

const generateJsCode = (formId: string, profileId: number) => {
  const stylesUrl = SemanticFormS.createSemanticFormStylesUrl(profileId, formId);
  const scriptUrl = SemanticFormS.createSemanticFormScriptUrl(profileId, formId);
  const stylesContent = `<link rel="stylesheet" type="text/css" href="${stylesUrl}">\n`;
  return jsEmbeddingCodeSnippet.replace('~!~CUSTOM_SCRIPT~!~', scriptUrl).replace('~!~CUSTOM_STYLES~!~', stylesContent);
};

const generateJsCodeV2 = (formId: string, profileId: number) => {
  // Omit unnecessary param for prod
  const baseUrlAttr = S3_CONNECT_URL !== 'https://connect.bookitup.de' ? `\n  data-base-url="${S3_CONNECT_URL}"` : '';

  return `<div id="bookitup-form"></div>
<script
  src="https://connect.bookitup.de/script-form.js"${baseUrlAttr}
  data-form-id="${formId}"
  data-profile-id="${profileId}">
</script>`;
};

const addCustomItem = async (formId: string, customItem: FormCustomItem): Promise<SemanticForm | null> => {
  const res = await ajaxActions.put(`${BASE_URL}/semanticform/${formId}/item?customItem=${customItem}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const previewAvailable = (form: SemanticForm | null): boolean => {
  if (form === null) {
    return false;
  }
  const { task_data } = form;
  if (task_data.length === 0) {
    return false;
  }

  for (let i = 0; i < task_data.length; i++) {
    const item: any = task_data[i];
    // Check if all columnRows have at least one element defined
    if (item.element.includes('ColumnRow') && (!item.childItems || item.childItems.every((e: unknown) => e === null))) {
      return false;
    }
  }
  return true;
};

const emailPresent = (task_data: TaskData[]): boolean => {
  if (task_data.length === 0) {
    return false;
  }
  return (
    task_data.find((item: any) => item.field_name && item.field_name.startsWith('customer.emailAddress')) !== undefined
  );
};

const endDatePresent = (task_data: TaskData[]): boolean => {
  if (task_data.length === 0) {
    return false;
  }
  return task_data.find((item: any) => item.field_name && item.field_name.startsWith('end.date')) !== undefined;
};

const resolveFormMetadata = (task_data: TaskData[]) => {
  const metadata = {
    emailPresent: false,
    endDatePresent: false,
    hcapthaPresent: false,
  };
  task_data.forEach((item: any) => {
    if (item.field_name) {
      if (item.field_name.startsWith('customer.emailAddress')) {
        metadata.emailPresent = true;
      }
      if (item.field_name.startsWith('end.date')) {
        metadata.endDatePresent = true;
      }
      if (item.field_name.startsWith('HCaptcha_')) {
        metadata.hcapthaPresent = true;
      }
    }
  });
  return metadata;
};

export const SemanticFormS = {
  HCAPTCHA_FIELD,
  create,
  copy,
  getForm,
  getForms,
  patch,
  patchStyles,
  deleteForm,
  createSemanticFormScriptUrl,
  createSemanticFormIframeUrl,
  createSemanticFormStylesUrl,
  syncFormFiles,
  generateIframeCode,
  createStandaloneSemanticFormIframeUrl,
  createSemanticFormIframeUrlV2,
  generateIframeCodeV2,
  generateJsCode,
  generateJsCodeV2,
  addCustomItem,
  previewAvailable,
  emailPresent,
  endDatePresent,
  resolveFormMetadata,
};
